import React, { useState, useEffect } from 'react';
import { Edit3, Save, Loader2, RotateCcw, MessageSquare, Settings, BarChart3 } from 'lucide-react';
import request from '../../api/request';
import { getOrderAnalysisConfig, updateOrderAnalysisConfig } from '../../api/ai';

type TabType = 'whatsapp' | 'ops' | 'order-analysis';

const AiPromptManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('whatsapp');

  // WhatsApp 助理提示词
  const [whatsappPrompt, setWhatsappPrompt] = useState('');
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappSaving, setWhatsappSaving] = useState(false);

  // 运营助理提示词
  const [opsPrompt, setOpsPrompt] = useState('');
  const [opsLoading, setOpsLoading] = useState(false);
  const [opsSaving, setOpsSaving] = useState(false);

  // 订单分析智能体提示词与配置
  const [orderAnalysisPrompt, setOrderAnalysisPrompt] = useState('');
  const [filterCsNumbers, setFilterCsNumbers] = useState(true);
  const [orderAnalysisLoading, setOrderAnalysisLoading] = useState(false);
  const [orderAnalysisSaving, setOrderAnalysisSaving] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchCurrentTabPrompt();
  }, [activeTab]);

  const fetchCurrentTabPrompt = async () => {
    setError('');
    setSuccess('');

    if (activeTab === 'whatsapp' && !whatsappPrompt) {
      setWhatsappLoading(true);
      try {
        const { data } = await request.get('/api/ai/whatsapp/instructions');
        setWhatsappPrompt((data && data.instructions) || '');
      } catch (err) {
        setError('无法获取 WhatsApp 助理提示词');
        console.error(err);
      } finally {
        setWhatsappLoading(false);
      }
    } else if (activeTab === 'ops' && !opsPrompt) {
      setOpsLoading(true);
      try {
        const { data } = await request.get('/api/ai/ops/instructions');
        setOpsPrompt((data && data.instructions) || '');
      } catch (err) {
        setError('无法获取运营助理提示词');
        console.error(err);
      } finally {
        setOpsLoading(false);
      }
    } else if (activeTab === 'order-analysis' && !orderAnalysisPrompt) {
      setOrderAnalysisLoading(true);
      try {
        const config = await getOrderAnalysisConfig();
        setOrderAnalysisPrompt(config.instructions);
        setFilterCsNumbers(config.filterCsNumbers);
      } catch (err) {
        setError('无法获取订单分析智能体配置');
        console.error(err);
      } finally {
        setOrderAnalysisLoading(false);
      }
    }
  };

  const handleRefresh = async () => {
    setError('');
    setSuccess('');

    if (activeTab === 'whatsapp') {
      setWhatsappLoading(true);
      try {
        const { data } = await request.get('/api/ai/whatsapp/instructions');
        setWhatsappPrompt((data && data.instructions) || '');
      } catch (err) {
        setError('无法获取 WhatsApp 助理提示词');
      } finally {
        setWhatsappLoading(false);
      }
    } else if (activeTab === 'ops') {
      setOpsLoading(true);
      try {
        const { data } = await request.get('/api/ai/ops/instructions');
        setOpsPrompt((data && data.instructions) || '');
      } catch (err) {
        setError('无法获取运营助理提示词');
      } finally {
        setOpsLoading(false);
      }
    } else if (activeTab === 'order-analysis') {
      setOrderAnalysisLoading(true);
      try {
        const config = await getOrderAnalysisConfig();
        setOrderAnalysisPrompt(config.instructions);
        setFilterCsNumbers(config.filterCsNumbers);
      } catch (err) {
        setError('无法获取订单分析智能体配置');
      } finally {
        setOrderAnalysisLoading(false);
      }
    }
  };

  const handleSave = async () => {
    setSuccess('');
    setError('');

    if (activeTab === 'whatsapp') {
      setWhatsappSaving(true);
      try {
        await request.put('/api/ai/whatsapp/instructions', { instructions: whatsappPrompt });
        setSuccess('WhatsApp 助理提示词已更新');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('保存失败');
        console.error(err);
      } finally {
        setWhatsappSaving(false);
      }
    } else if (activeTab === 'ops') {
      setOpsSaving(true);
      try {
        await request.put('/api/ai/ops/instructions', { instructions: opsPrompt });
        setSuccess('运营助理提示词已更新');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('保存失败');
        console.error(err);
      } finally {
        setOpsSaving(false);
      }
    } else if (activeTab === 'order-analysis') {
      setOrderAnalysisSaving(true);
      try {
        await updateOrderAnalysisConfig(orderAnalysisPrompt, filterCsNumbers);
        setSuccess('订单分析智能体配置已更新');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('保存失败');
        console.error(err);
      } finally {
        setOrderAnalysisSaving(false);
      }
    }
  };

  const tabs = [
    { id: 'whatsapp' as TabType, label: 'WhatsApp 助理', icon: MessageSquare, color: 'text-whatsapp-green' },
    { id: 'ops' as TabType, label: '运营助理', icon: Settings, color: 'text-blue-500' },
    { id: 'order-analysis' as TabType, label: '订单分析', icon: BarChart3, color: 'text-orange-500' },
  ];

  const getCurrentPrompt = () => {
    switch (activeTab) {
      case 'whatsapp': return whatsappPrompt;
      case 'ops': return opsPrompt;
      case 'order-analysis': return orderAnalysisPrompt;
    }
  };

  const setCurrentPrompt = (value: string) => {
    switch (activeTab) {
      case 'whatsapp': setWhatsappPrompt(value); break;
      case 'ops': setOpsPrompt(value); break;
      case 'order-analysis': setOrderAnalysisPrompt(value); break;
    }
  };

  const isLoading = () => {
    switch (activeTab) {
      case 'whatsapp': return whatsappLoading;
      case 'ops': return opsLoading;
      case 'order-analysis': return orderAnalysisLoading;
    }
  };

  const isSaving = () => {
    switch (activeTab) {
      case 'whatsapp': return whatsappSaving;
      case 'ops': return opsSaving;
      case 'order-analysis': return orderAnalysisSaving;
    }
  };

  const getDescription = () => {
    switch (activeTab) {
      case 'whatsapp':
        return '修改 WhatsApp 助理豆包的系统提示词，这将影响 AI 如何回复客户。';
      case 'ops':
        return '修改运营助理的系统提示词，用于生成活动文案、客服话术等。';
      case 'order-analysis':
        return '修改订单分析智能体的系统提示词，用于自动分析订单并判断是否需要通知客服。';
    }
  };

  if (isLoading()) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-background text-foreground animate-in fade-in slide-in-from-bottom-4">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2 mb-2">
            <Edit3 className="w-6 h-6 text-accent" />
            AI 提示词管理
          </h2>
          <p className="text-muted-foreground">{getDescription()}</p>
        </div>

        {/* Tab 导航 */}
        <div className="flex space-x-1 bg-muted/50 p-1 rounded-lg">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${activeTab === tab.id
                  ? 'bg-card text-foreground shadow-md'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/80'
                }`}
            >
              <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? tab.color : ''}`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* 提示词编辑区域 */}
        <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${tabs.find(t => t.id === activeTab)?.color}`}>
              {tabs.find(t => t.id === activeTab)?.label}
            </h3>

            <div className="flex items-center gap-4">
              {activeTab === 'order-analysis' && (
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={filterCsNumbers}
                    onChange={(e) => setFilterCsNumbers(e.target.checked)}
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-orange-500"></div>
                  <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">过滤客服号码</span>
                </label>
              )}

              <button
                onClick={handleRefresh}
                className="p-2 hover:bg-muted rounded-full text-muted-foreground hover:text-foreground transition-colors"
                title="重置为当前服务器配置"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                系统提示词 (System Instructions)
              </label>
              <textarea
                value={getCurrentPrompt()}
                onChange={(e) => setCurrentPrompt(e.target.value)}
                className="w-full h-96 p-4 bg-muted/30 border border-border rounded-md focus:ring-2 focus:ring-primary/50 focus:outline-none font-mono text-sm resize-y"
                placeholder="输入提示词..."
              />
              <p className="text-xs text-muted-foreground mt-2">
                {activeTab === 'order-analysis'
                  ? '提示：订单分析智能体需要返回 JSON 格式，包含 needs_notification、reason 和 action 字段。'
                  : '提示：你可以定义 AI 的角色、语气、回复限制以及可以使用的工具说明。'
                }
              </p>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center gap-2">
                {error && <span className="text-destructive text-sm">{error}</span>}
                {success && <span className="text-whatsapp-green text-sm">{success}</span>}
              </div>
              <button
                onClick={handleSave}
                disabled={isSaving()}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors shadow-md shadow-primary/20"
              >
                {isSaving() ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                保存更改
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiPromptManager;
